import * as fabric from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as api from '../../../../../../api/generated';
import { KendoLoading } from '../../../../../../components';
import ListItemCreateButton from '../../../../../../components/custom/ListItemCreateButton';
import { ILocationItem } from '../../../../../../components/custom/LocationTree';
import { useConfirmation } from '../../../../../../components/dialogs';
import { IWindowComponentProps, useWindow } from '../../../../../../components/windows/WindowContext';
import * as enums from '../../../../../../enums';
import { locationType } from '../../../../../../enums';
import * as models from '../../../../../../models';
import { default as LocationConfigDoorScreenLocationsCreateWindow } from './LocationConfigDoorScreenLocationsCreateWindow';

interface LocationConfigDoorScreenLocationsWindowProps {
    doorScreenId: number;
}

export interface ILocationDisplayItem {
    id: number;
    locationId: number;
    locationName: string | null;
    locationType: enums.LocationType;
}

const LocationConfigDoorScreenLocationsWindow: FC<IWindowComponentProps<LocationConfigDoorScreenLocationsWindowProps>> = (props) => {
    const {
        componentProps: { doorScreenId },
        onClose
    } = props;

    const [selectedLocations, setSelectedLocations] = useState<ILocationDisplayItem[] | null>(null);
    const [unSelectedLocations, setUnselectedLocations] = useState<ILocationItem[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const { openWindow } = useWindow();
    const { confirmAsync } = useConfirmation();

    const handleApprove = async (id: number) => {
        try {
            const { success } = await confirmAsync('Įrašo ištrynimas', 'Ar tikrai norite nebevaizduoti lokacijos durų ekrane?');

            if (!success) {
                return;
            }

            await api.administration.deleteDoorScreenDisplayLocation(id);
            toast.success('Lokacija sėkmingai atskirta nuo durų ekrano vaizdavimo');
            fetchData();
        } catch (ex) {
            toast.error('Lokacijos nuo durų ekrano atskirti nepavyko');
        }
    };

    const _allItemsColumns = [
        {
            key: 'column0',
            name: 'Veiksmai',
            minWidth: 70,
            maxWidth: 70,
            isResizable: true,
            onRender: (item: models.IDoorScreen) => {
                return (
                    <fabric.IconButton
                        onClick={async () => await handleApprove(item.id)}
                        iconProps={{
                            iconName: 'Trash'
                        }}
                        styles={{
                            root: {
                                margin: '0px, 2px',
                                color: fabric.getTheme().palette.red
                            }
                        }}
                    />
                );
            }
        },
        {
            key: 'column1',
            name: 'Lokacijos pavadinimas',
            fieldName: 'locationName',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true
        },
        {
            key: 'column2',
            name: 'Lokacijos tipas',
            fieldName: 'locationType',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (item: ILocationDisplayItem) => enums.locationType.format(item.locationType)
        }
    ];

    const fetchData = async () => {
        try {
            const [allLocations, doorScreenDisplayLocations] = await Promise.all([
                api.administration.getLocations(null),
                api.administration.getDoorScreenDisplayLocations(doorScreenId)
            ]);
            var selectedLocations: ILocationDisplayItem[] = [];
            var unselectedLocations: ILocationItem[] = [];

            allLocations
                .filter((x) => x.locationType === locationType.Cabinet || x.locationType === locationType.Room)
                .forEach((x) => {
                    let wasLocationSelected = false;
                    doorScreenDisplayLocations &&
                        doorScreenDisplayLocations.forEach((y) => {
                            if (y.locationId === x.id) {
                                selectedLocations.push({
                                    id: y.id,
                                    locationId: y.locationId,
                                    locationName: x.name,
                                    locationType: x.locationType
                                });
                                wasLocationSelected = true;
                            }
                        });
                    !wasLocationSelected && unselectedLocations.push(x);
                });

            setSelectedLocations(selectedLocations);
            setUnselectedLocations(unselectedLocations);
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };
    useEffect(() => {
        fetchData();
    }, []);

    const openLocationConfigDoorScreenLocationsCreateWindow = async () => {
        try {
            LocationConfigDoorScreenLocationsCreateWindow.defaultProps = {
                componentProps: {
                    fetchData: fetchData,
                    unselectedLocations: unSelectedLocations,
                    doorScreenId: doorScreenId
                }
            };
            openWindow({
                title: 'Lokacijos priskyrimas durų ekranui',
                component: LocationConfigDoorScreenLocationsCreateWindow,
                isDialogBehaviour: true,
                width: 800,
                height: 400
            });
        } catch (ex) {
            toast.error(ex.error[0]);
        }
    };

    return (
        <>
            {isLoading ? (
                <KendoLoading />
            ) : (
                <>
                    <div className="ms-Grid">
                        <div style={{ textAlign: 'center' }}>
                            <fabric.Text style={{ fontWeight: 'lighter' }} variant="xxLarge">
                                Durų ekrane atvaizduojamų lokacijų pasirinkimas
                            </fabric.Text>
                        </div>
                        {unSelectedLocations && (
                            <div className="ms-Grid-row" style={{ marginTop: '25px' }}>
                                <fabric.Stack horizontal horizontalAlign="space-between" style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <div>
                                        <fabric.Label>Nuoroda į įrenginio rodomą vaizdą</fabric.Label>
                                        <fabric.Link
                                            disabled={selectedLocations == null || selectedLocations.length < 1}
                                            target="_blank"
                                            href={`${window.location.origin}/display/doorScreen/${doorScreenId}`}
                                        >
                                            <fabric.Icon style={{ fontSize: '30px' }} iconName="PageArrowRight" />
                                        </fabric.Link>
                                    </div>
                                    <div>
                                        <ListItemCreateButton
                                            openCreateModal={openLocationConfigDoorScreenLocationsCreateWindow}
                                            text={unSelectedLocations.length < 1 ? 'Visos lokacijos yra pasirinktos' : 'Pridėti'}
                                            disabled={unSelectedLocations.length < 1}
                                        />
                                    </div>
                                </fabric.Stack>
                            </div>
                        )}

                        {!selectedLocations && <KendoLoading />}
                        {selectedLocations && (
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-md12">
                                    <fabric.DetailsList
                                        items={selectedLocations}
                                        columns={_allItemsColumns}
                                        checkboxVisibility={fabric.CheckboxVisibility.hidden}
                                        selectionMode={fabric.SelectionMode.none}
                                        disableSelectionZone={true}
                                        setKey={'2'}
                                        enterModalSelectionOnTouch={true}
                                        selectionPreservedOnEmptyClick={true}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default LocationConfigDoorScreenLocationsWindow;
